import React, {FC} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Box, BoxProps, Button, Flex, Heading, Skeleton, Stack, Text} from 'components/ui/chakra';
import CommonCareSearchController from 'puddleglum/api/patient/CommonCareSearchController';

type CommonCareSearchItem = {
	label: string;
	search: string;
	type: 'specialty' | 'booking_request' | 'direct_booking' | 'voucher';
};

interface CommonCareSearchesProps extends BoxProps {
	onSelectSearch: (selection: string) => void;
}

const CommonCareSearches: FC<CommonCareSearchesProps> = ({onSelectSearch, ...boxProps}) => {
	const {data: commonSearches, isLoading} = useQuery(['common-care-searches'], async () => {
		const reply = await CommonCareSearchController.index();
		return reply.data as CommonCareSearchItem[];
	});

	const visibleCommonSearches = commonSearches ?? [];

	if (isLoading) {
		return (
			<Box {...boxProps}>
				<Stack spacing={4}>
					<Heading size='sm' color='gray.700'>
						Common Searches
					</Heading>
					<Flex wrap='wrap' gap={2}>
						{Array.from({length: 12}).map((_, index) => (
							<Skeleton
								// eslint-disable-next-line react/no-array-index-key
								key={`search-skeleton-${index}`}
								h='2.25rem'
								w={{base: '8rem', md: `${8 + (index % 4) * 2}rem`}}
								borderRadius='full'
							/>
						))}
					</Flex>
				</Stack>
			</Box>
		);
	}

	if (visibleCommonSearches.length === 0) {
		return null;
	}

	return (
		<Box {...boxProps}>
			<Stack spacing={4}>
				<Heading size='sm' color='gray.700'>
					Common Searches
				</Heading>

				<Flex wrap='wrap' gap={2}>
					{visibleCommonSearches.map((item) => (
						<Button
							key={`${item.type}-${item.search}`}
							onClick={() => onSelectSearch(item.search)}
							size='xs'
							variant='outline'
							borderRadius='full'
							px={3}
							py={0}
							minH='none'
							fontWeight='medium'
						>
							<Text fontSize='xs' lineHeight='short'>
								{item.label}
							</Text>
						</Button>
					))}
				</Flex>
			</Stack>
		</Box>
	);
};

export default CommonCareSearches;
